import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Mail, Phone, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { fmtINR, SITE } from "@/lib/site";

export default function Confirmation() {
  const { ref } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.title = "Booking Confirmation | Sagar Darshan Guest House";
    api.booking(ref).then(setBooking).catch(() => setError(true));
  }, [ref]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-sand px-5 text-center">
        <p className="font-display text-3xl text-navy">Booking not found</p>
        <p className="mt-3 text-sm text-navy/60">Please check your booking reference or contact us.</p>
        <Link to="/" data-testid="confirmation-home-btn" className="btn-outline-dark mt-8">Back to Home</Link>
      </div>
    );
  }

  if (!booking) return <div className="min-h-screen bg-sand" />;

  const isConfirmed = booking.status === "confirmed";
  const rows = [
    ["Booking ID", booking.booking_ref],
    ["Guest Name", booking.name],
    ["Rooms", booking.room_name],
    ["Check-in", booking.check_in],
    ["Check-out", booking.check_out],
    ["Guests", `${booking.adults} Adult${booking.adults > 1 ? "s" : ""}${booking.children ? `, ${booking.children} Child${booking.children > 1 ? "ren" : ""}` : ""}`],
    ["Nights", booking.nights],
    [isConfirmed ? "Amount Paid" : "Amount Payable", fmtINR(booking.total)],
    ...(booking.utr ? [["UPI Reference (UTR)", booking.utr]] : []),
    ...(booking.payment_id ? [["Payment Reference", booking.payment_id]] : []),
    ["Status", isConfirmed ? "Confirmed" : "Payment Under Verification"],
  ];

  return (
    <div data-testid="confirmation-page" className="grain relative flex min-h-screen items-center justify-center bg-navy px-5 py-32">
      <motion.div
        initial={{ opacity: 0, y: 32, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-2xl border border-white/10 bg-sand p-8 shadow-2xl sm:p-12"
      >
        <div className="flex flex-col items-center text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 text-gold-dark">
            {isConfirmed ? <CheckCircle2 size={34} strokeWidth={1.4} /> : <Clock size={34} strokeWidth={1.4} />}
          </span>
          <p className="overline-label mt-6">{isConfirmed ? "Booking Confirmed" : "Payment Under Verification"}</p>
          <h1 className="mt-3 font-display text-3xl tracking-tight text-navy sm:text-4xl">
            {isConfirmed ? "We look forward to welcoming you" : "Booking received - verifying your payment"}
          </h1>
          <p data-testid="confirmation-ref" className="mt-4 inline-block bg-navy px-5 py-2 font-body text-sm font-semibold tracking-[0.3em] text-gold">
            {booking.booking_ref}
          </p>
        </div>

        <div className="mt-10 divide-y divide-navy/10 border-y border-navy/10" data-testid="confirmation-details">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between gap-6 py-3.5 text-sm">
              <span className="text-navy/50">{k}</span>
              <span className="text-right font-medium text-navy">{v}</span>
            </div>
          ))}
        </div>

        <p className="mt-6 flex items-center justify-center gap-2 text-center text-xs text-navy/60">
          <Mail size={13} className="text-gold-dark" />
          {isConfirmed
            ? `A confirmation email has been sent to ${booking.email}`
            : `A booking receipt has been emailed to ${booking.email}. Your booking will be confirmed once our team verifies your UPI payment.`}
        </p>

        <div className="mt-9 grid gap-3 sm:grid-cols-2">
          <a href={`tel:${SITE.phoneHref}`} data-testid="confirmation-call-btn" className="btn-outline-dark !py-3.5">
            <Phone size={14} /> Call the Guest House
          </a>
          <Link to="/" data-testid="confirmation-home-link" className="btn-primary !py-3.5">Back to Home</Link>
        </div>
      </motion.div>
    </div>
  );
}
